<template>
    <!-- Renderless component - manages tour triggers based on route -->
</template>

<script setup>
import {watch, nextTick} from 'vue';
import {useRoute} from 'vue-router';
import {useAuthStore} from '@/stores/authStore';
import {hasSeen, startTour} from '@/composables/useProductTour';

const route = useRoute();
const authStore = useAuthStore();

// Only show tours for company users (not super_admin)
const isCompanyUser = () => authStore.user?.type === 'company';

// Tour registry: route name → tour module (lazy-loaded)
const tourRegistry = {
    'dashboard': () => import('@/tours/welcomeTour.js'),
    'live-view': () => import('@/tours/liveViewTour.js'),
    'monitoring-configs': () => import('@/tours/monitoringTour.js'),
};

// Delay before starting a tour (let the page render first)
const TOUR_DELAY_MS = 1200;

watch(() => route.name, async (routeName) => {
    if (!routeName || !isCompanyUser()) return;

    const loader = tourRegistry[routeName];
    if (!loader) return;

    const tourModule = await loader();
    if (hasSeen(tourModule.TOUR_NAME)) return;

    // Wait for the page to fully render before highlighting elements
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, TOUR_DELAY_MS));

    // Re-check after delay (user may have navigated away)
    if (route.name !== routeName) return;

    // For welcome tour, only show if the getting-started checklist is visible
    // (meaning they've completed the wizard but haven't finished all milestones)
    if (tourModule.TOUR_NAME === 'welcome') {
        const checklistEl = document.querySelector('.getting-started-card');
        // Filter steps: only include checklist step if it's visible
        const steps = tourModule.steps.filter(step => {
            if (step.element === '.getting-started-card' && !checklistEl) return false;
            return true;
        });
        startTour(tourModule.TOUR_NAME, steps);
    } else {
        startTour(tourModule.TOUR_NAME, tourModule.steps);
    }
}, {immediate: true});
</script>
